import { CommonModule, NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { UtilityService } from "@eforall/common";
import { DialogService, LinkButtonWidget, WebLink } from "@eforall/dashboard";
import { AccApplicationDetails, Accelerator, LanguageId } from "@interfaces";
import { DomainDataService } from "../../../../services";
import { VideoDialog, VideoDialogData } from "../../dialog/video-dialog/video.dialog";
import { PersonDialog } from "../../person-dialog";
import { AccApplicationDialogDataService } from "../acc-application-dialog-data.service";

const BUSINESS_STAGE: { [key: number]: string } = {
	0: '0- Idea',
	1: '1- Initial Stage',
	2: '2- Stable'
}

@Component({
	selector: 'acc-application-overview-content-dialog-part',
	standalone: true,
	imports: [
		NgTemplateOutlet,
		LinkButtonWidget,
		CommonModule
	],
	templateUrl: './overview-content-dialog.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './overview-content-dialog.part.scss'
})
export class OverviewContentDialogPart {

	private readonly util = inject(UtilityService);
	public readonly dialogDataService = inject(AccApplicationDialogDataService);
	private readonly dialogService = inject(DialogService);
	private readonly domain = inject(DomainDataService);

	public readonly accApplicationDetails = computed(() => {
		const data = this.dialogDataService.dialogData();
		if (!data) return undefined;

		const selectedAccSlots = new Set(this.dialogDataService.accWithScheduleSlots()?.filter(a => a.applicantSelectedAccSlot).map(a => a.meetingAcceleratorDisplayText));

		return {
			...data.details,
			language: data.details.languageId === LanguageId.Espanol ? 'Spanish' : 'English',
			videoUploadedStatus: this.getVideoUploadedStatus(data.details),
			industry: this.domain.data().industriesMap[data.details.industryId]?.name,
			businessStageString: BUSINESS_STAGE[data.details.businessStage],
			accApplicationStatus: this.domain.data().accApplicationStatusesMap[data.details.accApplicationStatusId]?.name,
			seatfOfferedStatus: this.getSeatOfferedStatus(data.details, data.accelerators),
			availableMeetingTimes: selectedAccSlots.size ? selectedAccSlots : ['None'],
			participant: {
				...data.details.participant,
				formattedVideoUploadedUTC: data.details.participant.videoUploadedUTC ? this.dialogDataService.util.date.formatUTC(data.details.participant.videoUploadedUTC, 'MMM D, YYYY', 'H:MM AM EST', 'en-US') : undefined,
				employmentStatus: this.domain.data().employeeStatusesMap[data.details.participant.employmentStatusId]?.name,
			},
			coParticipants: data.details.coParticipants?.map((v) => ({
				...v,
				formattedVideoUploadedUTC: v.videoUploadedUTC ? this.dialogDataService.util.date.formatUTC(v.videoUploadedUTC, 'MMM D, YYYY', 'H:MM AM EST', 'en-US') : undefined,
				employmentStatus: this.domain.data().employeeStatusesMap[v.employmentStatusId]?.name,
			})),
			invitedParticipants: data.details.invitedParticipants?.map((v) => ({
				...v,
				formattedInviteCode: this.util.text.formatCode(v.inviteCode)
			}))
		}
	});

	public readonly websiteLink = computed(() => {
		const details = this.accApplicationDetails();
		if (!details?.website) return undefined;

		const link: WebLink = {
			overrideText: 'Open',
			linkType: 'webLink',
			url: details.website,
			webLinkTypeId: 0, // Generic web link
		};
		return link;
	});

	private getVideoUploadedStatus(accApp: AccApplicationDetails): 'Yes' | 'Partial' | 'No' {
		const hasMainParticipantVideo = Boolean(accApp.participant?.uniqueVideoId);
		const hasCoParticipants = accApp.coParticipants.length > 0;

		// If there are no co-participants, return based on the applicant's video status
		if (!hasCoParticipants) {
			return hasMainParticipantVideo ? 'Yes' : 'No';
		}

		const allCoParticipantsHaveVideo = accApp.coParticipants.every(p => p.uniqueVideoId);
		const someCoParticipantsHaveVideo = accApp.coParticipants.some(p => p.uniqueVideoId);

		// If the applicant has a video, return 'Yes' if all co-participants have videos, otherwise 'Partial'
		if (hasMainParticipantVideo) {
			return allCoParticipantsHaveVideo ? 'Yes' : 'Partial';
		}

		// If the applicant does not have a video, return 'Partial' if any co-participant has a video, otherwise 'No'
		return someCoParticipantsHaveVideo ? 'Partial' : 'No';
	};

	async openVideoDialog(personId: number, uniqueVideoId: number) {
		// Open the video dialog with the provided uniqueVideoId
		if (uniqueVideoId) {
			const dialogData: VideoDialogData = {
				type: 'applicationParticipant',
				personId,
				uniqueVideoId
			}
			this.dialogService.showCustom(VideoDialog, { data: dialogData }, 500, 550);
		}
	};

	openPersonDialog(personId: number) {
		this.dialogService.showCustom(PersonDialog, { data: { personId, toEdit: false } }, 800, 800);
	}


	getSeatOfferedStatus(accApp: AccApplicationDetails, accelerators: Accelerator[]): string {
		if (accApp.offeredSeatAccId) {
			const status = accApp.accApplicationStatusId == 8 || accApp.accApplicationStatusId == 9 ? 'Accepted' : 'Pending';
			const acc = accelerators.find(a => a.accId === accApp.offeredSeatAccId);
			let accDisplayName = '';
			if (acc) {
				accDisplayName = this.dialogDataService.util.date.formatAccMeetingSlot(acc.meetingStartSlot, acc.standardFourTimeZoneId).acceleratorDisplayTime;
			}

			return `${status} - ${accDisplayName} (accId: ${accApp.offeredSeatAccId})`;
		} else {
			return 'Not Offered';
		}
	}
}